// src/components/SalesDashboard/QuotationDetail.js
import React, { useCallback, useEffect, useState } from 'react';
import {
  ChevronLeft, ChevronDown, ChevronUp, CheckCircle, XCircle,
  Send, Clock, AlertTriangle, FileText, RefreshCw, Loader, Eye, Download, Mail,
} from 'lucide-react';
import api from '../api';

// ── Styles ────────────────────────────────────────────────────────────────────
const S = `
  .qd-wrap { max-width: 1100px; margin: 0 auto; font-family: 'Inter', sans-serif; color: #000; }
  .qd-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 24px; flex-wrap: wrap; gap: 12px; }
  .qd-header-left h2 { font-size: 22px; font-weight: 800; color: #1e293b; margin: 0 0 6px; }
  .qd-header-left .qd-sub { font-size: 13px; color: #1a1a1a; }
  .qd-stepper-wrap { margin: 0 0 20px; padding: 14px 16px; border: 1px solid #e2e8f0; border-radius: 12px; background: #f8fafc; overflow-x: auto; }
  .qd-stepper { min-width: 680px; display: flex; align-items: center; gap: 8px; }
  .qd-step { display: inline-flex; align-items: center; gap: 8px; }
  .qd-step-dot { width: 24px; height: 24px; border-radius: 999px; display: inline-flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 800; border: 1.5px solid #cbd5e1; background: white; color: #1a1a1a; }
  .qd-step-label { font-size: 11px; font-weight: 700; color: #1a1a1a; white-space: nowrap; }
  .qd-step-connector { width: 28px; height: 2px; border-radius: 2px; background: #cbd5e1; }

  .qd-badge {
    display: inline-block; padding: 4px 14px; border-radius: 20px;
    font-size: 12px; font-weight: 700; white-space: nowrap;
  }

  .qd-version-tabs { display: flex; gap: 6px; margin-bottom: 24px; flex-wrap: wrap; }
  .qd-vtab {
    padding: 6px 16px; border-radius: 8px; font-size: 13px; font-weight: 700;
    cursor: pointer; border: 2px solid #e2e8f0; background: white; color: #1a1a1a;
    transition: all .15s;
  }
  .qd-vtab.active { background: #6366f1; border-color: #6366f1; color: white; }
  .qd-vtab.old { background: #f8fafc; color: #1a1a1a; font-style: italic; }
  .qd-vtab.old:hover { background: #f1f5f9; }

  .qd-grid { display: grid; grid-template-columns: 320px 1fr; gap: 20px; align-items: start; }
  @media(max-width:800px) { .qd-grid { grid-template-columns: 1fr; } }

  .qd-card { background: white; border-radius: 16px; border: 1px solid #e2e8f0; padding: 24px; margin-bottom: 20px; }
  .qd-card-title { font-size: 14px; font-weight: 700; color: #1e293b; margin: 0 0 16px; display: flex; align-items: center; gap: 8px; }
  .qd-info-row { display: flex; justify-content: space-between; padding: 7px 0; border-bottom: 1px solid #f1f5f9; font-size: 13px; }
  .qd-info-row:last-child { border-bottom: none; }
  .qd-info-label { color: #1a1a1a; font-weight: 600; }
  .qd-info-val { color: #1e293b; font-weight: 600; text-align: right; }

  .qd-table { width: 100%; border-collapse: collapse; font-size: 13px; }
  .qd-table thead tr { background: #f8fafc; }
  .qd-table th { padding: 10px 14px; text-align: left; font-size: 11px; font-weight: 700; color: #1a1a1a; text-transform: uppercase; letter-spacing: .4px; border-bottom: 2px solid #e2e8f0; }
  .qd-table td { padding: 12px 14px; border-bottom: 1px solid #f1f5f9; color: #1e293b; vertical-align: top; }
  .qd-table tbody tr:last-child td { border-bottom: none; }

  .qd-bom-toggle { display: flex; align-items: center; gap: 4px; cursor: pointer; color: #6366f1; font-size: 11px; font-weight: 600; background: none; border: none; padding: 4px 0; margin-top: 4px; }
  .qd-bom-inner { background: #f8fafc; border-radius: 8px; padding: 10px; margin-top: 8px; font-size: 11px; }
  .qd-bom-inner table { width: 100%; border-collapse: collapse; }
  .qd-bom-inner td, .qd-bom-inner th { padding: 4px 8px; border-bottom: 1px solid #e2e8f0; }
  .qd-bom-inner th { color: #1a1a1a; font-weight: 600; }

  .qd-totals { background: linear-gradient(135deg,#1e1b4b,#312e81); border-radius: 14px; padding: 20px 24px; color: white; }
  .qd-totals-row { display: flex; justify-content: space-between; padding: 5px 0; font-size: 14px; }
  .qd-totals-divider { border: none; border-top: 1px solid rgba(255,255,255,.2); margin: 8px 0; }
  .qd-totals-grand { font-size: 20px; font-weight: 800; }

  .qd-actions { display: flex; gap: 10px; flex-wrap: wrap; }
  .qd-btn {
    display: inline-flex; align-items: center; gap: 7px; padding: 10px 20px;
    border-radius: 10px; font-size: 13px; font-weight: 700; cursor: pointer;
    border: none; transition: opacity .15s; white-space: nowrap;
  }
  .qd-btn:hover { opacity: .88; }
  .qd-btn:disabled { opacity: .5; cursor: not-allowed; }
  .qd-btn-primary { background: linear-gradient(135deg,#6366f1,#4f46e5); color: white; }
  .qd-btn-success { background: linear-gradient(135deg,#10b981,#059669); color: white; }
  .qd-btn-danger { background: linear-gradient(135deg,#ef4444,#dc2626); color: white; }
  .qd-btn-warning { background: linear-gradient(135deg,#f59e0b,#d97706); color: white; }
  .qd-btn-outline { background: white; color: #1a1a1a; border: 1.5px solid #e2e8f0; }
  .qd-btn-indigo { background: #eef2ff; color: #6366f1; border: 1.5px solid #c7d2fe; }

  .qd-version-snap { background: #f8fafc; border-radius: 12px; padding: 16px; }
  .qd-version-snap table { width: 100%; border-collapse: collapse; font-size: 12px; }
  .qd-version-snap td, .qd-version-snap th { padding: 6px 10px; border-bottom: 1px solid #e2e8f0; }
  .qd-version-snap th { color: #1a1a1a; font-weight: 600; }

  .qd-modal-overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,.4); z-index: 1000;
    display: flex; align-items: center; justify-content: center;
  }
  .qd-modal {
    background: white; border-radius: 16px; padding: 28px; width: 400px; max-width: 94vw;
    box-shadow: 0 24px 64px rgba(0,0,0,.2);
  }
  .qd-modal h3 { margin: 0 0 16px; font-size: 17px; font-weight: 800; color: #1e293b; }
  .qd-modal textarea {
    width: 100%; padding: 10px 14px; border: 1.5px solid #e2e8f0; border-radius: 10px;
    font-size: 14px; resize: vertical; outline: none; box-sizing: border-box; color: #000;
  }
  .qd-modal textarea:focus { border-color: #6366f1; }
  .qd-modal input[type="email"] {
    width: 100%; padding: 10px 14px; border: 1.5px solid #e2e8f0; border-radius: 10px;
    font-size: 14px; outline: none; box-sizing: border-box; margin-bottom: 10px; color: #000;
  }
  .qd-modal input[type="email"]:focus { border-color: #6366f1; }
  .qd-modal-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 16px; }
  .qd-modal-label { font-size: 12px; font-weight: 600; color: #1a1a1a; margin-bottom: 5px; display: block; }
  .qd-modal-info { font-size: 12px; color: #000; background: #eef2ff; border-radius: 8px; padding: 8px 12px; margin-top: 10px; display: flex; align-items: center; gap: 6px; }

  .qd-snapshot-label { font-size: 11px; font-weight: 600; color: #1a1a1a; text-transform: uppercase; letter-spacing: .5px; margin-bottom: 8px; }
`;

const STATUS_CONFIG = {
  DRAFT:            { bg: '#f1f5f9', color: '#475569' },
  PENDING_APPROVAL: { bg: '#fef3c7', color: '#b45309' },
  APPROVED:         { bg: '#d1fae5', color: '#065f46' },
  SENT_TO_CLIENT:   { bg: '#dbeafe', color: '#1d4ed8' },
  SENT:             { bg: '#dbeafe', color: '#1d4ed8' },
  ACCEPTED:         { bg: '#d1fae5', color: '#047857' },
  REJECTED:         { bg: '#fee2e2', color: '#b91c1c' },
  CLIENT_ACCEPTED:  { bg: '#d1fae5', color: '#047857' },
  CLIENT_REJECTED:  { bg: '#fee2e2', color: '#b91c1c' },
  EXPIRED:          { bg: '#f1f5f9', color: '#9ca3af' },
};

const normalizeStatus = (status) => {
  if (status === 'SENT') return 'SENT_TO_CLIENT';
  if (status === 'ACCEPTED') return 'CLIENT_ACCEPTED';
  if (status === 'REJECTED') return 'CLIENT_REJECTED';
  return status;
};

const STEPPER_STEPS = [
  { key: 'DRAFT', label: 'Draft' },
  { key: 'PENDING_APPROVAL', label: 'Pending Admin Approval' },
  { key: 'APPROVED', label: 'Admin Approved' },
  { key: 'SENT_TO_CLIENT', label: 'Sent to Client' },
  { key: 'CLIENT_DECISION', label: 'Client Accepted/Rejected' },
];

const stepStatus = (currentStatus, stepKey) => {
  const s = normalizeStatus(currentStatus);
  const accepted = s === 'CLIENT_ACCEPTED';
  const rejected = s === 'CLIENT_REJECTED';
  const currentOrder = {
    DRAFT: 0,
    PENDING_APPROVAL: 1,
    APPROVED: 2,
    SENT_TO_CLIENT: 3,
    CLIENT_ACCEPTED: 4,
    CLIENT_REJECTED: 4,
  };
  const stepOrder = {
    DRAFT: 0,
    PENDING_APPROVAL: 1,
    APPROVED: 2,
    SENT_TO_CLIENT: 3,
    CLIENT_DECISION: 4,
  };
  const cur = currentOrder[s] ?? 0;
  const me = stepOrder[stepKey] ?? 0;
  if (stepKey === 'CLIENT_DECISION' && rejected) return 'rejected';
  if (cur > me) return 'done';
  if (cur === me) return (accepted || rejected) ? 'done' : 'active';
  return 'todo';
};

const fmt = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { bg: '#f1f5f9', color: '#475569' };
  return <span className="qd-badge" style={{ background: cfg.bg, color: cfg.color }}>{status?.replace('_', ' ')}</span>;
};

const Spinner = () => (
  <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
    <div style={{ width: 40, height: 40, border: '3px solid #e2e8f0', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin .8s linear infinite' }} />
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
  </div>
);

function StatusStepper({ status }) {
  return (
    <div className="qd-stepper-wrap">
      <div className="qd-stepper">
        {STEPPER_STEPS.map((step, idx) => {
          const state = stepStatus(status, step.key);
          const isDone = state === 'done';
          const isActive = state === 'active';
          const isRejected = state === 'rejected';
          const dotStyle = isRejected
            ? { background: '#fee2e2', borderColor: '#fca5a5', color: '#b91c1c' }
            : isDone
              ? { background: '#dcfce7', borderColor: '#86efac', color: '#166534' }
              : isActive
                ? { background: '#dbeafe', borderColor: '#93c5fd', color: '#1d4ed8' }
                : {};
          const labelStyle = isRejected
            ? { color: '#b91c1c' }
            : isDone
              ? { color: '#166534' }
              : isActive
                ? { color: '#1d4ed8' }
                : {};
          return (
            <React.Fragment key={step.key}>
              <div className="qd-step">
                <span className="qd-step-dot" style={dotStyle}>{isDone || isRejected ? '✓' : idx + 1}</span>
                <span className="qd-step-label" style={labelStyle}>{step.label}</span>
              </div>
              {idx < STEPPER_STEPS.length - 1 && (
                <span
                  className="qd-step-connector"
                  style={isDone ? { background: '#86efac' } : {}}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

// ── Reject Modal ──────────────────────────────────────────────────────────────
function PromptModal({ open, title, label, onClose, onConfirm, loading }) {
  const [text, setText] = useState('');
  if (!open) return null;
  return (
    <div className="qd-modal-overlay" onClick={onClose}>
      <div className="qd-modal" onClick={e => e.stopPropagation()}>
        <h3>{title}</h3>
        <textarea rows={3} placeholder={label} value={text} onChange={e => setText(e.target.value)} />
        <div className="qd-modal-actions">
          <button className="qd-btn qd-btn-outline" onClick={onClose}>Cancel</button>
          <button className="qd-btn qd-btn-danger" disabled={loading} onClick={() => onConfirm(text)}>
            {loading ? <Loader size={14} /> : null} Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Convert to Order Modal ────────────────────────────────────────────────────
function ConvertToOrderModal({ open, quotationNo, onClose, onConfirm, loading }) {
  const [deliveryDate, setDeliveryDate] = useState('');
  const [advance, setAdvance] = useState('');
  const [priority, setPriority] = useState('3');
  const [instructions, setInstructions] = useState('');

  if (!open) return null;
  return (
    <div className="qd-modal-overlay" onClick={onClose}>
      <div className="qd-modal" style={{ width: 460 }} onClick={e => e.stopPropagation()}>
        <h3>Convert {quotationNo} to Sales Order</h3>

        <label className="qd-modal-label">Priority</label>
        <select
          value={priority}
          onChange={e => setPriority(e.target.value)}
          style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #e2e8f0', borderRadius: 10, fontSize: 14, marginBottom: 10, outline: 'none' }}
        >
          <option value="1">1 — Urgent</option>
          <option value="2">2 — High</option>
          <option value="3">3 — Normal</option>
        </select>

        <label className="qd-modal-label">Expected Delivery Date</label>
        <input
          type="date"
          value={deliveryDate}
          onChange={e => setDeliveryDate(e.target.value)}
          style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #e2e8f0', borderRadius: 10, fontSize: 14, marginBottom: 10, outline: 'none', boxSizing: 'border-box' }}
        />

        <label className="qd-modal-label">Advance Received (₹)</label>
        <input
          type="number"
          placeholder="0.00"
          value={advance}
          onChange={e => setAdvance(e.target.value)}
          min="0"
          style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #e2e8f0', borderRadius: 10, fontSize: 14, marginBottom: 10, outline: 'none', boxSizing: 'border-box' }}
        />

        <label className="qd-modal-label">Special Instructions (optional)</label>
        <textarea
          rows={2}
          placeholder="Any production instructions…"
          value={instructions}
          onChange={e => setInstructions(e.target.value)}
        />

        <div className="qd-modal-actions">
          <button className="qd-btn qd-btn-outline" onClick={onClose} disabled={loading}>Cancel</button>
          <button
            className="qd-btn qd-btn-success"
            disabled={loading}
            onClick={() => onConfirm({ deliveryDate, advance: parseFloat(advance) || 0, priority: parseInt(priority), instructions })}
          >
            {loading ? <Loader size={14} /> : <CheckCircle size={14} />}
            {loading ? 'Converting…' : 'Confirm & Create Order'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Send Email Modal ───────────────────────────────────────────────────────────
function SendEmailModal({ open, quotationNo, defaultEmail, onClose, onSend, loading }) {
  const [toEmail, setToEmail] = useState(defaultEmail || '');
  const [message, setMessage] = useState('We look forward to your confirmation.');

  useEffect(() => { if (open) setToEmail(defaultEmail || ''); }, [open, defaultEmail]);

  if (!open) return null;
  return (
    <div className="qd-modal-overlay" onClick={onClose}>
      <div className="qd-modal" style={{ width: 460 }} onClick={e => e.stopPropagation()}>
        <h3><Mail size={16} style={{ verticalAlign: 'middle', marginRight: 8 }} />Send Quotation {quotationNo}</h3>
        <label className="qd-modal-label">To (Email)</label>
        <input
          type="email"
          placeholder="customer@email.com"
          value={toEmail}
          onChange={e => setToEmail(e.target.value)}
        />
        <label className="qd-modal-label">Message</label>
        <textarea
          rows={3}
          placeholder="Custom message to customer…"
          value={message}
          onChange={e => setMessage(e.target.value)}
        />
        <div className="qd-modal-info">
          <FileText size={13} /> Quotation PDF will be attached automatically.
        </div>
        <div className="qd-modal-actions">
          <button className="qd-btn qd-btn-outline" onClick={onClose} disabled={loading}>Cancel</button>
          <button
            className="qd-btn qd-btn-primary"
            disabled={loading || !toEmail.trim()}
            onClick={() => onSend(toEmail.trim(), message)}
          >
            {loading ? <Loader size={14} /> : <Send size={14} />}
            {loading ? 'Sending…' : 'Send Quotation'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── BOM in detail ─────────────────────────────────────────────────────────────
function BomCell({ bom }) {
  const [open, setOpen] = useState(false);
  if (!bom) return <span style={{ color: '#94a3b8' }}>—</span>;
  const stages = bom.stages || [];
  return (
    <div>
      <div style={{ fontSize: 12 }}>
        Metal: <strong>{bom.metal_weight || 0}g</strong>
      </div>
      {stages.length > 0 && (
        <>
          <button className="qd-bom-toggle" onClick={() => setOpen(o => !o)}>
            {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            BOM ({stages.length} materials)
          </button>
          {open && (
            <div className="qd-bom-inner">
              <table>
                <thead>
                  <tr><th>Material</th><th>Qty</th><th>Unit</th><th>Est. Cost</th></tr>
                </thead>
                <tbody>
                  {stages.map((s, i) => (
                    <tr key={i}>
                      <td>{s.material}</td>
                      <td>{s.qty}</td>
                      <td>{s.unit}</td>
                      <td>₹{fmt(s.estimated_cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function QuotationDetail({ quotationId, user, onBack, onCreateNewVersion, onConvertToOrder }) {
  const isAdmin = !!(
    user?.is_admin ||
    user?.role === 'org_admin' ||
    user?.access_matrix?.some(a => a.role === 'org_admin')
  );
  const [qt, setQt] = useState(null);
  const [items, setItems] = useState([]);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [viewVersion, setViewVersion] = useState(null); // null = current
  const [modal, setModal] = useState(null); // 'reject' | 'reject-customer' | 'send' | 'convert'
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [convertLoading, setConvertLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/quotations/${quotationId}`);
      setQt(res.data.quotation);
      setItems(res.data.items);
      setVersions(res.data.versions);
      setViewVersion(null);
    } catch (e) {
      setError('Failed to load quotation.');
    }
    setLoading(false);
  }, [quotationId]);

  useEffect(() => { load(); }, [load]);

  const action = async (fn, successMsg) => {
    setActionLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await fn();
      const msg = typeof successMsg === 'function' ? successMsg(res) : successMsg;
      setSuccess(msg);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Action failed.');
    }
    setActionLoading(false);
  };

  const handleSendEmail = async (toEmail, message) => {
    setSendLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await api.post(`/quotations/${quotationId}/send`, { to_email: toEmail, message });
      if (res.data.success) {
        setSuccess(`Quotation sent to ${res.data.sent_to}`);
        setQt(prev => ({ ...prev, status: 'SENT_TO_CLIENT', sent_at: new Date().toISOString(), sent_to_email: toEmail }));
        setModal(null);
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Send failed. Check SMTP configuration.');
    }
    setSendLoading(false);
  };

  const handleDownloadPdf = async () => {
    setActionLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await api.get(`/quotations/${quotationId}/pdf`, {
        responseType: 'blob',
      });
      const contentDisposition = res.headers['content-disposition'] || '';
      const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
      const fallbackName = `Quotation_${qt?.quotation_no || quotationId}.pdf`;
      const fileName = fileNameMatch?.[1] || fallbackName;
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      setSuccess(`Downloaded ${fileName}`);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to download quotation PDF.');
    }
    setActionLoading(false);
  };

  const handleConvertToOrder = async ({ deliveryDate, advance, priority, instructions }) => {
    setConvertLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await api.post(`/sales/orders/from-quotation/${quotationId}`, {
        expected_delivery_date: deliveryDate ? `${deliveryDate}T00:00:00Z` : null,
        advance_received: advance,
        priority,
        special_instructions: instructions,
      });
      setModal(null);
      setSuccess(`Order ${res.data.order_code} created successfully!`);
      await load();
      if (onConvertToOrder) onConvertToOrder(res.data.order_id);
    } catch (e) {
      setError(e.response?.data?.error || 'Conversion failed.');
    }
    setConvertLoading(false);
  };

  const handleClientAccept = async () => {
    setActionLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await api.post(`/quotations/${quotationId}/client-accept`);
      const salesOrder = res?.data?.sales_order;
      const orderCode = salesOrder?.order_code;

      setSuccess(`Client accepted quotation. Sales order ${orderCode || ''} created.`);
      await load();

      if (salesOrder?.id && onConvertToOrder) {
        onConvertToOrder(salesOrder.id);
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Client acceptance failed.');
    }
    setActionLoading(false);
  };

  if (loading) return <><style>{S}</style><Spinner /></>;
  if (!qt) return <><style>{S}</style><div style={{ color: '#dc2626', padding: 24 }}>Quotation not found.</div></>;
  const normalizedStatus = normalizeStatus(qt.status);

  const currentVersion = qt.current_version;
  const viewingOld = viewVersion !== null && viewVersion !== currentVersion;
  const versionSnapshot = viewingOld ? versions.find(v => v.version_no === viewVersion) : null;

  // Items to display
  const isReadOnly = viewingOld;

  return (
    <>
      <style>{S}</style>

      {/* Reject modal */}
      <PromptModal
        open={modal === 'reject'}
        title="Reject Quotation"
        label="Enter rejection reason…"
        loading={actionLoading}
        onClose={() => setModal(null)}
        onConfirm={(reason) => {
          setModal(null);
          action(() => api.post(`/quotations/${quotationId}/reject`, { reason }), 'Quotation rejected. Returned to DRAFT.');
        }}
      />

      {/* Reject by customer modal */}
      <PromptModal
        open={modal === 'reject-customer'}
        title="Mark as Rejected by Customer"
        label="Enter reason (optional)…"
        loading={actionLoading}
        onClose={() => setModal(null)}
        onConfirm={(reason) => {
          setModal(null);
          action(() => api.post(`/quotations/${quotationId}/client-reject`, { notes: reason }), 'Quotation marked as rejected by client.');
        }}
      />

      {/* Convert to Order modal */}
      <ConvertToOrderModal
        open={modal === 'convert'}
        quotationNo={qt.quotation_no}
        loading={convertLoading}
        onClose={() => setModal(null)}
        onConfirm={handleConvertToOrder}
      />

      {/* Send Email modal */}
      <SendEmailModal
        open={modal === 'send'}
        quotationNo={qt.quotation_no}
        defaultEmail={qt.customer_email || qt.sent_to_email || ''}
        loading={sendLoading}
        onClose={() => setModal(null)}
        onSend={handleSendEmail}
      />

      <div className="qd-wrap">
        <StatusStepper status={normalizedStatus} />
        {/* Back */}
        <button
          style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: '#6366f1', fontWeight: 700, fontSize: 13, cursor: 'pointer', marginBottom: 20, padding: 0 }}
          onClick={onBack}
        >
          <ChevronLeft size={16} /> Back to Quotations
        </button>

        {/* Header */}
        <div className="qd-header">
          <div className="qd-header-left">
            <h2>{qt.quotation_no}</h2>
            <div className="qd-sub">
              <StatusBadge status={normalizedStatus} />
              <span style={{ marginLeft: 12, color: '#1a1a1a' }}>{qt.subject}</span>
              {normalizedStatus === 'DRAFT' && (
                <span style={{ marginLeft: 12, color: '#1a1a1a', fontSize: 12 }}>Draft — not submitted yet</span>
              )}
              {normalizedStatus === 'PENDING_APPROVAL' && (
                <span style={{ marginLeft: 12, color: '#b45309', fontSize: 12 }}>Awaiting manager approval</span>
              )}
              {normalizedStatus === 'APPROVED' && qt.approved_at && (
                <span style={{ marginLeft: 12, color: '#059669', fontSize: 12 }}>
                  Approved on {new Date(qt.approved_at).toLocaleDateString()}
                </span>
              )}
              {normalizedStatus === 'SENT_TO_CLIENT' && qt.sent_at && (
                <span style={{ marginLeft: 12, color: '#1d4ed8', fontSize: 12 }}>
                  Sent to {qt.sent_to_email} on {new Date(qt.sent_at).toLocaleDateString()}
                </span>
              )}
              {normalizedStatus === 'CLIENT_ACCEPTED' && (
                <span style={{ marginLeft: 12, color: '#047857', fontSize: 12 }}>Customer accepted</span>
              )}
              {normalizedStatus === 'CLIENT_REJECTED' && (
                <span style={{ marginLeft: 12, color: '#b91c1c', fontSize: 12 }}>
                  Rejected{qt.rejection_reason ? ` — ${qt.rejection_reason}` : ''}
                </span>
              )}
            </div>
          </div>
          <button className="qd-btn qd-btn-outline" onClick={load} disabled={loading}>
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        {/* Alerts */}
        {error && (
          <div style={{ background: '#fef2f2', border: '1.5px solid #fca5a5', borderRadius: 10, padding: '12px 16px', color: '#dc2626', fontSize: 13, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertTriangle size={16} /> {error}
          </div>
        )}
        {success && (
          <div style={{ background: '#f0fdf4', border: '1.5px solid #86efac', borderRadius: 10, padding: '12px 16px', color: '#15803d', fontSize: 13, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <CheckCircle size={16} /> {success}
          </div>
        )}

        {/* Rejection banner — shown when manager returned quotation to draft */}
        {normalizedStatus === 'DRAFT' && qt.rejection_reason && (
          <div style={{ background: '#fefce8', border: '1.5px solid #fde047', borderRadius: 10, padding: '12px 16px', color: '#854d0e', fontSize: 13, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertTriangle size={16} color="#ca8a04" />
            <span><strong>Returned by manager:</strong> {qt.rejection_reason}</span>
          </div>
        )}

        {/* Version Tabs */}
        {versions.length > 0 && (
          <div className="qd-version-tabs">
            {versions.map(v => (
              <button
                key={v.version_no}
                className={`qd-vtab ${
                  (viewVersion === v.version_no || (viewVersion === null && v.version_no === currentVersion))
                    ? 'active'
                    : v.version_no < currentVersion ? 'old' : ''
                }`}
                onClick={() => setViewVersion(v.version_no === currentVersion ? null : v.version_no)}
                title={v.change_summary}
              >
                V{v.version_no} {v.version_no === currentVersion ? '(current)' : ''}
              </button>
            ))}
          </div>
        )}

        {viewingOld && (
          <div style={{ background: '#fefce8', border: '1.5px solid #fde047', borderRadius: 10, padding: '10px 16px', marginBottom: 16, fontSize: 13, color: '#854d0e', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Eye size={14} /> Viewing read-only snapshot of Version {viewVersion}
            {versionSnapshot?.change_summary && <span> · {versionSnapshot.change_summary}</span>}
          </div>
        )}

        {/* Main layout */}
        <div className="qd-grid">
          {/* Left: Customer + Totals */}
          <div>
            <div className="qd-card">
              <div className="qd-card-title"><FileText size={16} color="#6366f1" /> Customer Info</div>
              <div className="qd-info-row">
                <span className="qd-info-label">Name</span>
                <span className="qd-info-val">{qt.customer_name}</span>
              </div>
              <div className="qd-info-row">
                <span className="qd-info-label">Email</span>
                <span className="qd-info-val">{qt.customer_email || '—'}</span>
              </div>
              <div className="qd-info-row">
                <span className="qd-info-label">Valid Until</span>
                <span className="qd-info-val">{qt.valid_until}</span>
              </div>
              {qt.rejection_reason && (
                <div className="qd-info-row">
                  <span className="qd-info-label">Rejection Note</span>
                  <span className="qd-info-val" style={{ color: '#dc2626' }}>{qt.rejection_reason}</span>
                </div>
              )}
            </div>

            <div className="qd-card">
              <div className="qd-card-title">Pricing</div>
              <div className="qd-info-row">
                <span className="qd-info-label">Subtotal</span>
                <span className="qd-info-val">₹{fmt(qt.subtotal)}</span>
              </div>
              <div className="qd-info-row">
                <span className="qd-info-label">Discount ({qt.discount_pct}%)</span>
                <span className="qd-info-val" style={{ color: '#ef4444' }}>-₹{fmt(qt.discount_amount)}</span>
              </div>
              <div className="qd-info-row">
                <span className="qd-info-label">GST ({qt.tax_pct}%)</span>
                <span className="qd-info-val" style={{ color: '#f59e0b' }}>+₹{fmt(qt.tax_amount)}</span>
              </div>
              <div className="qd-info-row" style={{ borderTop: '2px solid #e2e8f0', marginTop: 4, paddingTop: 10 }}>
                <span style={{ fontWeight: 800, color: '#1e293b' }}>TOTAL</span>
                <span style={{ fontWeight: 800, fontSize: 18, color: '#1e1b4b' }}>₹{fmt(qt.total_amount)}</span>
              </div>
            </div>

            {qt.terms_and_conditions && (
              <div className="qd-card">
                <div className="qd-card-title">Terms & Conditions</div>
                <p style={{ fontSize: 13, color: '#000', margin: 0, lineHeight: 1.6 }}>{qt.terms_and_conditions}</p>
              </div>
            )}
          </div>

          {/* Right: Items + Actions */}
          <div>
            {/* Actions — only on current version */}
            {!isReadOnly && (
              <div className="qd-card">
                <div className="qd-card-title">Actions</div>
                <ActionButtons
                  status={normalizedStatus}
                  loading={actionLoading}
                  qtId={quotationId}
                  isManager={isAdmin}
                  convertedToOrderId={qt.converted_to_order_id}
                  onSubmitApproval={() => action(
                    async () => {
                      try {
                        return await api.post(`/quotations/${quotationId}/submit-for-approval`);
                      } catch (err) {
                        if (err?.response?.status === 404) {
                          return api.post(`/quotations/${quotationId}/submit-approval`);
                        }
                        throw err;
                      }
                    },
                    (res) => res?.data?.message || 'Submitted for approval.'
                  )}
                  onApprove={() => action(() => api.post(`/quotations/${quotationId}/approve`), 'Quotation approved.')}
                  onReject={() => setModal('reject')}
                  onSend={() => setModal('send')}
                  onClientAccept={handleClientAccept}
                  onMarkRejected={() => setModal('reject-customer')}
                  onNewVersion={() => onCreateNewVersion(quotationId)}
                  onConvertToOrder={() => setModal('convert')}
                  onDownloadPdf={handleDownloadPdf}
                />
              </div>
            )}

            {/* Items table */}
            <div className="qd-card">
              <div className="qd-card-title">
                Design Items
                {viewingOld && <span style={{ fontSize: 11, color: '#1a1a1a', marginLeft: 8 }}>Snapshot V{viewVersion}</span>}
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table className="qd-table">
                  <thead>
                    <tr>
                      <th>Design</th>
                      <th>Metal Type</th>
                      <th>Qty</th>
                      <th>Unit Price</th>
                      <th>Total</th>
                      <th>BOM</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewingOld
                      ? (versionSnapshot?.snapshot?.items || []).map((it, i) => (
                          <tr key={i}>
                            <td>
                              <DesignThumb
                                imageUrl={it.image_url}
                                code={it.design_code}
                                name={it.design_name}
                              />
                            </td>
                            <td>{it.metal_type || '—'}</td>
                            <td>{it.qty}</td>
                            <td>₹{fmt(it.unit_price)}</td>
                            <td style={{ fontWeight: 700 }}>₹{fmt(it.total)}</td>
                            <td>—</td>
                          </tr>
                        ))
                      : items.map(it => (
                          <tr key={it.id}>
                            <td>
                              <DesignThumb
                                imageUrl={it.image_url}
                                code={it.design_code}
                                name={it.design_name}
                                requirements={it.special_requirements}
                              />
                            </td>
                            <td>{it.metal_type}</td>
                            <td>{it.quantity}</td>
                            <td>
                              <div>₹{fmt(it.unit_price)}</div>
                              <div style={{ fontSize: 10, color: '#94a3b8' }}>
                                Metal: ₹{fmt(it.unit_metal_price)} + Making: ₹{fmt(it.making_charges)}
                                {it.stone_charges > 0 && ` + Stone: ₹${fmt(it.stone_charges)}`}
                              </div>
                            </td>
                            <td style={{ fontWeight: 700 }}>₹{fmt(it.total_price)}</td>
                            <td><BomCell bom={it.bom_estimate} /></td>
                          </tr>
                        ))
                    }
                  </tbody>
                </table>
              </div>
            </div>

            {/* Version History */}
            {versions.length > 0 && (
              <div className="qd-card">
                <div className="qd-card-title"><Clock size={16} color="#6366f1" /> Version History</div>
                {versions.map(v => (
                  <VersionAccordion key={v.version_no} v={v} current={currentVersion} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// ── Action Buttons per status ─────────────────────────────────────────────────
function ActionButtons({ status, loading, qtId, isManager, convertedToOrderId, onSubmitApproval, onApprove, onReject, onSend, onClientAccept, onMarkRejected, onNewVersion, onConvertToOrder, onDownloadPdf }) {
  const L = loading;

  // "Convert to Order" button — shown when approved or accepted and not yet converted
  const ConvertBtn = !convertedToOrderId ? (
    <button className="qd-btn qd-btn-success" disabled={L} onClick={onConvertToOrder}>
      {L ? <Loader size={14} /> : <CheckCircle size={14} />} Convert to Order
    </button>
  ) : (
    <div style={{ fontSize: 13, color: '#10b981', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
      <CheckCircle size={14} /> Already converted to Order #{convertedToOrderId}
    </div>
  );

  return (
    <div className="qd-actions">
      {/* Salesperson: submit draft for approval */}
      {status === 'DRAFT' && (
        <button className="qd-btn qd-btn-primary" disabled={L} onClick={onSubmitApproval}>
          {L ? <Loader size={14} /> : <Send size={14} />} Submit for Approval
        </button>
      )}
      {status === 'DRAFT' && (
        <button className="qd-btn qd-btn-outline" onClick={onDownloadPdf}>
          <Download size={14} /> Preview PDF
        </button>
      )}

      {/* Manager: approve or reject pending quotation */}
      {status === 'PENDING_APPROVAL' && isManager && (
        <>
          <button className="qd-btn qd-btn-success" disabled={L} onClick={onApprove}>
            {L ? <Loader size={14} /> : <CheckCircle size={14} />} Approve
          </button>
          <button className="qd-btn qd-btn-danger" disabled={L} onClick={onReject}>
            {L ? <Loader size={14} /> : <XCircle size={14} />} Reject with Reason
          </button>
        </>
      )}
      {status === 'PENDING_APPROVAL' && !isManager && (
        <div style={{ fontSize: 13, color: '#b45309', display: 'flex', alignItems: 'center', gap: 6 }}>
          <Clock size={14} /> Awaiting manager approval — no actions available.
        </div>
      )}

      {/* APPROVED: send to customer + client decision */}
      {status === 'APPROVED' && (
        <>
          <button className="qd-btn qd-btn-indigo" disabled={L} onClick={onSend}>
            {L ? <Loader size={14} /> : <Mail size={14} />} Send to Customer
          </button>
          <button className="qd-btn qd-btn-success" disabled={L} onClick={onClientAccept}>
            {L ? <Loader size={14} /> : <CheckCircle size={14} />} Mark Client Accepted
          </button>
          <button className="qd-btn qd-btn-danger" disabled={L} onClick={onMarkRejected}>
            {L ? <Loader size={14} /> : <XCircle size={14} />} Mark Client Rejected
          </button>
          <button className="qd-btn qd-btn-outline" onClick={onDownloadPdf}>
            <Download size={14} /> Download PDF
          </button>
        </>
      )}

      {status === 'SENT_TO_CLIENT' && (
        <>
          <button className="qd-btn qd-btn-success" disabled={L} onClick={onClientAccept}>
            {L ? <Loader size={14} /> : <CheckCircle size={14} />} Mark Client Accepted
          </button>
          <button className="qd-btn qd-btn-danger" disabled={L} onClick={onMarkRejected}>
            {L ? <Loader size={14} /> : <XCircle size={14} />} Mark Client Rejected
          </button>
          <button className="qd-btn qd-btn-indigo" disabled={L} onClick={onSend}>
            <RefreshCw size={14} /> Resend Email
          </button>
          <button className="qd-btn qd-btn-warning" disabled={L} onClick={onNewVersion}>
            <RefreshCw size={14} /> New Version
          </button>
          <button className="qd-btn qd-btn-outline" onClick={onDownloadPdf}>
            <Download size={14} /> Download PDF
          </button>
        </>
      )}

      {/* CLIENT_ACCEPTED: already converted automatically */}
      {status === 'CLIENT_ACCEPTED' && (
        <>
          {ConvertBtn}
          <button className="qd-btn qd-btn-outline" onClick={onDownloadPdf}>
            <Download size={14} /> Download PDF
          </button>
        </>
      )}

      {(status === 'CLIENT_REJECTED' || status === 'EXPIRED') && (
        <>
          <div style={{ fontSize: 13, color: '#1a1a1a', fontStyle: 'italic' }}>
            This quotation is {status.toLowerCase()}. Create a new version or a new quotation.
          </div>
          <button className="qd-btn qd-btn-outline" onClick={onDownloadPdf}>
            <Download size={14} /> Download PDF
          </button>
        </>
      )}
    </div>
  );
}

// ── Version Accordion ─────────────────────────────────────────────────────────
function VersionAccordion({ v, current }) {
  const [open, setOpen] = useState(false);
  const snap = v.snapshot || {};
  return (
    <div style={{ borderBottom: '1px solid #f1f5f9', paddingBottom: 10, marginBottom: 10 }}>
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', padding: '4px 0' }}
        onClick={() => setOpen(o => !o)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontWeight: 700, color: v.version_no === current ? '#6366f1' : '#1a1a1a', fontSize: 13 }}>
            V{v.version_no} {v.version_no === current ? '(current)' : '(archived)'}
          </span>
          <span style={{ fontSize: 12, color: '#1a1a1a' }}>{v.change_summary}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 11, color: '#94a3b8' }}>{v.created_at ? new Date(v.created_at).toLocaleDateString() : ''}</span>
          {open ? <ChevronUp size={14} color="#94a3b8" /> : <ChevronDown size={14} color="#94a3b8" />}
        </div>
      </div>
      {open && (
        <div className="qd-version-snap" style={{ marginTop: 8 }}>
          <div className="qd-snapshot-label">Snapshot — V{v.version_no}</div>
          {snap.items && snap.items.length > 0 && (
            <table>
              <thead>
                <tr><th>Design</th><th>Qty</th><th>Unit Price</th><th>Total</th></tr>
              </thead>
              <tbody>
                {snap.items.map((it, i) => (
                  <tr key={i}>
                    <td>{it.design_code} {it.design_name && `— ${it.design_name}`}</td>
                    <td>{it.qty}</td>
                    <td>₹{Number(it.unit_price || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                    <td style={{ fontWeight: 600 }}>₹{Number(it.total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div style={{ marginTop: 8, fontSize: 12, color: '#1a1a1a' }}>
            Total: <strong>₹{Number(snap.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</strong>
            {snap.valid_until && <span style={{ marginLeft: 16 }}>Valid until: {snap.valid_until}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

function DesignThumb({ imageUrl, code, name, requirements }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={name || code || 'Design'}
          style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover', border: '1px solid #e2e8f0', flexShrink: 0 }}
        />
      ) : (
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: 8,
            border: '1px solid #e2e8f0',
            background: '#f8fafc',
            color: '#94a3b8',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 10,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          IMG
        </div>
      )}
      <div>
        <div style={{ fontWeight: 600 }}>{code}</div>
        <div style={{ fontSize: 11, color: '#1a1a1a' }}>{name}</div>
        {requirements && (
          <div style={{ fontSize: 11, color: '#6366f1', marginTop: 2 }}>{requirements}</div>
        )}
      </div>
    </div>
  );
}
